import useScrollStore from '@store/scrollStore';
import { useCallback, useEffect, useRef } from 'react';

const SCROLL_ADJUSTMENT_INTERVAL_MS = 150;
const SCROLL_ADJUSTMENT_THRESHOLD_PX = 5;
const IMAGE_LOADING_TIMEOUT_MS = 2000;
const MOBILE_GNB_OFFSET_ADJUSTMENT_PX = 10;
const USER_INTERACTION_EVENTS = [
  'wheel',
  'touchstart',
  'pointerdown',
  'keydown',
] as const;

let latestScrollRequestId = 0;
let stopActiveScrollAdjustment: (() => void) | null = null;

export const startPromotionSectionScrollRequest = (): number => {
  latestScrollRequestId += 1;
  stopActiveScrollAdjustment?.();
  return latestScrollRequestId;
};

export const isPromotionSectionScrollRequestActive = (
  requestId: number
): boolean => requestId === latestScrollRequestId;

export const getMobilePromotionScrollOffset = (
  headerHeight: number,
  mainGnbHeight: number
): number => headerHeight + mainGnbHeight - MOBILE_GNB_OFFSET_ADJUSTMENT_PX;

interface UsePromotionSectionPositionParams {
  readonly isDesktop?: boolean;
  readonly maxWaitTime?: number;
}

interface UsePromotionSectionPositionResult {
  readonly scrollToElement: (
    element: HTMLElement,
    shouldContinue?: () => boolean
  ) => Promise<void>;
  readonly stopScrollAdjustment: () => void;
}

const waitForElementImages = (element: HTMLElement): Promise<void> =>
  new Promise(resolve => {
    const images = element.querySelectorAll('img');
    if (images.length === 0) {
      resolve();
      return;
    }

    let loadedCount = 0;
    const timeout = setTimeout(resolve, IMAGE_LOADING_TIMEOUT_MS);

    const checkAllLoaded = (): void => {
      loadedCount += 1;
      if (loadedCount === images.length) {
        clearTimeout(timeout);
        resolve();
      }
    };

    images.forEach(image => {
      if (image.complete) {
        checkAllLoaded();
        return;
      }
      image.addEventListener('load', checkAllLoaded, { once: true });
      image.addEventListener('error', checkAllLoaded, { once: true });
    });
  });

const subscribeToUserInteraction = (
  onInteraction: () => void
): (() => void) => {
  USER_INTERACTION_EVENTS.forEach(eventName => {
    window.addEventListener(eventName, onInteraction, { passive: true });
  });

  return () => {
    USER_INTERACTION_EVENTS.forEach(eventName => {
      window.removeEventListener(eventName, onInteraction);
    });
  };
};

export const dispatchProgrammaticScrollEvent = (
  eventTarget: EventTarget,
  targetPosition: number
): void => {
  eventTarget.dispatchEvent(
    new CustomEvent('customScroll', {
      detail: {
        isCustomScrollRef: true,
        targetScrollTop: targetPosition,
        headerScrollDirection: 'scrollDown',
      },
    })
  );
};

/** URL 앵커와 기본 네비게이션이 같은 위치 계산과 보정 규칙을 사용하도록 묶은 훅 */
export const usePromotionSectionPosition = ({
  isDesktop,
  maxWaitTime = 5000,
}: UsePromotionSectionPositionParams): UsePromotionSectionPositionResult => {
  const { setManualScroll, setPendingNaviClick } = useScrollStore(state => ({
    setManualScroll: state.setManualScroll,
    setPendingNaviClick: state.setPendingNaviClick,
  }));
  const adjustTimerRef = useRef<NodeJS.Timeout | null>(null);
  const stopPositionAdjustmentRef = useRef<(() => void) | null>(null);
  const userInteractedRef = useRef(false);

  const calculateTargetPosition = useCallback(
    (element: HTMLElement, elementTop: number): number => {
      const megaPromotionPage = document.querySelector('.mega-promotion-page');
      const isApp = megaPromotionPage?.classList.contains('is-app');
      const isSubLayout = document.querySelector('.sub-layout');
      const isLpWrapSection = element.classList.contains('corner-lp-wrap');

      let targetPosition: number;

      if (isSubLayout) {
        const headerHeight =
          document.querySelector('#header')?.clientHeight || 0;

        if (isLpWrapSection) {
          targetPosition = elementTop - headerHeight;
        } else {
          const eventNavigationHeight =
            document.querySelector('.event-navigation')?.clientHeight || 0;
          targetPosition = elementTop - headerHeight - eventNavigationHeight;
        }
      } else if (isDesktop || isApp) {
        if (isLpWrapSection) {
          targetPosition = elementTop;
        } else {
          const eventNavigationHeight =
            document.querySelector('.event-navigation')?.clientHeight || 0;
          targetPosition = elementTop - eventNavigationHeight;
        }
      } else {
        const headerHeight =
          document.querySelector('#header')?.clientHeight || 0;
        const mainGnbHeight =
          document.querySelector('#main-gnb-bar')?.clientHeight || 0;

        if (isLpWrapSection) {
          targetPosition =
            elementTop - mainGnbHeight + MOBILE_GNB_OFFSET_ADJUSTMENT_PX;
        } else {
          const baseOffset = getMobilePromotionScrollOffset(
            headerHeight,
            mainGnbHeight
          );
          targetPosition = elementTop - baseOffset;
        }
      }

      return targetPosition;
    },
    [isDesktop]
  );

  const stopScrollAdjustment = useCallback((): void => {
    stopPositionAdjustmentRef.current?.();
  }, []);

  const adjustScrollPosition = useCallback(
    (element: HTMLElement): void => {
      stopScrollAdjustment();
      let adjustCount = 0;
      const maxAdjustments = Math.ceil(
        maxWaitTime / SCROLL_ADJUSTMENT_INTERVAL_MS
      );
      let unsubscribeUserInteraction = (): void => {};

      const stopAdjustment = (): void => {
        if (adjustTimerRef.current) {
          clearTimeout(adjustTimerRef.current);
          adjustTimerRef.current = null;
        }
        unsubscribeUserInteraction();
        setManualScroll(false);
        setPendingNaviClick(null);
        stopPositionAdjustmentRef.current = null;
        if (stopActiveScrollAdjustment === stopAdjustment) {
          stopActiveScrollAdjustment = null;
        }
      };

      const handleUserInteraction = (): void => {
        userInteractedRef.current = true;
        stopAdjustment();
      };

      const adjust = (): void => {
        if (userInteractedRef.current || adjustCount >= maxAdjustments) {
          stopAdjustment();
          return;
        }

        const currentScrollTop = window.pageYOffset;
        const elementTop =
          element.getBoundingClientRect().top + currentScrollTop;
        const targetPosition = calculateTargetPosition(element, elementTop);

        if (
          Math.abs(currentScrollTop - targetPosition) >
          SCROLL_ADJUSTMENT_THRESHOLD_PX
        ) {
          dispatchProgrammaticScrollEvent(window, targetPosition);
          window.scrollTo({ top: targetPosition, behavior: 'auto' });
        }

        adjustCount += 1;
        if (adjustCount < maxAdjustments && !userInteractedRef.current) {
          adjustTimerRef.current = setTimeout(
            adjust,
            SCROLL_ADJUSTMENT_INTERVAL_MS
          );
          return;
        }
        stopAdjustment();
      };

      stopPositionAdjustmentRef.current = stopAdjustment;
      stopActiveScrollAdjustment?.();
      stopActiveScrollAdjustment = stopAdjustment;
      unsubscribeUserInteraction = subscribeToUserInteraction(
        handleUserInteraction
      );
      adjustTimerRef.current = setTimeout(adjust, 100);
    },
    [
      calculateTargetPosition,
      maxWaitTime,
      setManualScroll,
      setPendingNaviClick,
      stopScrollAdjustment,
    ]
  );

  const scrollToElement = useCallback(
    async (
      element: HTMLElement,
      shouldContinue: () => boolean = () => true
    ): Promise<void> => {
      userInteractedRef.current = false;
      await waitForElementImages(element);
      if (!shouldContinue()) return;
      const currentScrollTop = window.pageYOffset;
      const elementTop = element.getBoundingClientRect().top + currentScrollTop;
      const targetPosition = calculateTargetPosition(element, elementTop);

      dispatchProgrammaticScrollEvent(window, targetPosition);
      window.scrollTo({ top: targetPosition, behavior: 'auto' });
      adjustScrollPosition(element);
    },
    [adjustScrollPosition, calculateTargetPosition]
  );

  useEffect(
    () => () => {
      stopScrollAdjustment();
      userInteractedRef.current = false;
    },
    [stopScrollAdjustment]
  );

  return { scrollToElement, stopScrollAdjustment };
};
